import { useQuery } from '@tanstack/react-query';
import { useInventorySource } from './InventorySource';
import LoadingSpinner from '../Shared/LoadingSpinner';
import { fmtMoney, fmtNumber, fmtDate } from './format';
import { c, t, sp, tbl } from '../../theme';

// The same part number held at more than one yard.
//
// Ranked by idle value, not total: a part moving at both places is stocked
// twice deliberately, while one sitting idle away from the yard that needs it
// is a transfer somebody could make this week. This is the only question in
// the doc's scope that the current export answers outright, with no feed we do
// not already have.
// With a cost center selected this narrows to parts THAT yard holds which also
// sit elsewhere — the buyer's question — while the value columns still span
// every yard, which is the reason to look at all.
export default function CrossYardPanel({ ccId }) {
  const { api, queryKey } = useInventorySource();
  const { data, isLoading, error } = useQuery({
    queryKey: queryKey('inventory-crossyard', ccId || 'all'),
    queryFn:  () => api.get('/inventory/crossyard', {
      params: { limit: 25, ...(ccId ? { cc_id: ccId } : {}) },
    }),
  });

  if (isLoading) return <div style={s.pad}><LoadingSpinner /></div>;
  if (error)     return <div style={s.pad}><div style={s.err}>{error.message}</div></div>;

  const parts = data?.parts || [];
  if (!parts.length) {
    return (
      <div style={s.empty}>
        {ccId
          ? 'Nothing this cost center holds is also stocked at another yard.'
          : 'No part number is held at more than one cost center.'}
      </div>
    );
  }

  return (
    <div style={s.scroll}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Part #</th>
            <th style={s.th}>Description</th>
            <th style={s.thR}>Yards</th>
            <th style={s.thR}>Qty</th>
            <th style={s.thR}>Total Value</th>
            <th style={s.thR}>Idle Value</th>
            <th style={s.th}>Last Issued Anywhere</th>
          </tr>
        </thead>
        <tbody>
          {parts.map((p) => (
            <tr key={p.part_number} style={s.tr}>
              <td style={s.td}>{p.part_number}</td>
              <td style={s.tdDesc} title={p.description}>{p.description}</td>
              <td style={s.tdR}>{fmtNumber(p.cost_center_count)}</td>
              <td style={s.tdR}>{fmtNumber(p.total_quantity)}</td>
              <td style={s.tdR}>{fmtMoney(p.total_value)}</td>
              <td style={{ ...s.tdR, color: p.idle_value > 0 ? c.warningText : c.textFaint }}>
                {p.idle_value > 0 ? fmtMoney(p.idle_value) : '—'}
              </td>
              <td style={s.td}>{fmtDate(p.last_used_anywhere)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const s = {
  scroll: { overflowX: 'auto' },
  table:  { width: '100%', borderCollapse: 'collapse' },
  th:     { ...tbl.th, whiteSpace: 'nowrap' },
  thR:    { ...tbl.thR, whiteSpace: 'nowrap' },
  tr:     { ...tbl.tr },
  td:     { ...tbl.td, whiteSpace: 'nowrap' },
  tdR:    { ...tbl.tdR },
  tdDesc: {
    ...tbl.td, maxWidth: '260px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  pad:   { padding: sp[5] },
  empty: { padding: sp[8], textAlign: 'center', ...t.bodySm, color: c.textFaint },
  err:   { ...t.bodySm, color: c.danger },
};
